import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";
import { Menu, X, Twitter, Instagram, Facebook, Youtube, Music } from "lucide-react";
import { useSiteContent } from "@/hooks/useSiteContent";
import { DEFAULTS } from "@/lib/content_defaults";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/programs", label: "Programs" },
  { to: "/players", label: "Players" },
  { to: "/coaches", label: "Coaches" },
  { to: "/recruiting", label: "Recruiting" },
  { to: "/events", label: "Events" },
  { to: "/pricing", label: "Pricing" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data: brand } = useSiteContent("brand", DEFAULTS.brand);
  const { data: info } = useSiteContent("contact_info", DEFAULTS.contact_info);

  const socials = [
    { url: info.social_x, Icon: Twitter, label: `X${info.social_x_handle ? ` — ${info.social_x_handle}` : ""}` },
    { url: info.social_instagram, Icon: Instagram, label: info.social_instagram_handle || "Instagram" },
    { url: info.social_facebook, Icon: Facebook, label: "Facebook" },
    { url: info.social_youtube, Icon: Youtube, label: "YouTube" },
    { url: info.social_tiktok, Icon: Music, label: "TikTok" },
  ].filter(s => s.url);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/85 border-b border-white/10 text-white">
        <div className="container-app flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-home-link">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt={brand.site_name} className="h-9 w-9 object-contain"/>
            ) : (
              <div className="w-9 h-9 border border-[color:var(--gold)] flex items-center justify-center font-heading font-black text-[color:var(--gold)]">{brand.monogram}</div>
            )}
            <div className="font-heading font-black uppercase tracking-widest text-sm leading-none">
              <div>{brand.site_name}</div>
              <div className="text-[10px] text-white/50 tracking-[0.25em]">{brand.site_subname}</div>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-6">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `font-heading uppercase tracking-widest text-sm hover:text-[color:var(--gold)] transition-colors ${isActive ? "text-[color:var(--gold)]" : "text-white/80"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            {user && user !== false ? (
              <>
                {user.role === "admin" && (<Link to="/admin" data-testid="nav-admin" className="font-heading uppercase text-sm tracking-widest text-[color:var(--gold)] hover:text-white">Admin</Link>)}
                <Link to="/dashboard" data-testid="nav-dashboard" className="font-heading uppercase text-sm tracking-widest text-white/80 hover:text-white">Dashboard</Link>
                <button data-testid="nav-logout" onClick={() => { logout(); navigate("/"); }} className="btn-ghost !py-2 !px-4">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="font-heading uppercase text-sm tracking-widest text-white/80 hover:text-white">Login</Link>
                <Link to="/register" data-testid="nav-register" className="btn-gold !py-2 !px-4">Join</Link>
              </>
            )}
          </div>
          <button data-testid="mobile-menu-toggle" className="lg:hidden text-white" onClick={() => setOpen(!open)}>
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-white/10 bg-black">
            <div className="container-app py-4 flex flex-col gap-3">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className="font-heading uppercase tracking-widest text-white/80 hover:text-[color:var(--gold)]">
                  {n.label}
                </NavLink>
              ))}
              <div className="pt-3 border-t border-white/10 flex gap-3 flex-wrap">
                {user && user !== false ? (
                  <>
                    {user.role === "admin" && (<Link to="/admin" onClick={() => setOpen(false)} className="btn-ghost !py-2 !px-4">Admin</Link>)}
                    <Link to="/dashboard" onClick={() => setOpen(false)} className="btn-ghost !py-2 !px-4">Dashboard</Link>
                    <button data-testid="mobile-logout" onClick={() => { logout(); setOpen(false); navigate("/"); }} className="btn-ghost !py-2 !px-4">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost !py-2 !px-4">Login</Link>
                    <Link to="/register" onClick={() => setOpen(false)} className="btn-gold !py-2 !px-4">Join</Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="section-dark border-t border-white/10 mt-0">
        <div className="container-app py-16 grid md:grid-cols-4 gap-10">
          <div>
            <div className="font-heading font-black uppercase tracking-widest text-2xl text-[color:var(--gold)]">{brand.monogram}</div>
            <div className="font-heading uppercase tracking-widest text-white/70 text-sm">{brand.site_name}</div>
            <p className="mt-4 text-white/60 text-sm leading-relaxed">{brand.footer_about}</p>
          </div>
          <div>
            <div className="label-tag mb-4">Programs</div>
            <ul className="space-y-2 text-white/70 text-sm">
              <li><Link to="/programs" className="hover:text-white">All Programs</Link></li>
              <li><Link to="/recruiting" className="hover:text-white">NCAA Recruiting</Link></li>
              <li><Link to="/events" className="hover:text-white">Events</Link></li>
              <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-tag mb-4">Contact</div>
            <ul className="space-y-2 text-white/70 text-sm">
              {info.email && <li><a href={`mailto:${info.email}`} className="hover:text-[color:var(--gold)]">{info.email}</a></li>}
              {info.phone_1 && <li><a href={`tel:${info.phone_1.replace(/\./g,'')}`} className="hover:text-[color:var(--gold)]">{info.phone_1}</a></li>}
              {info.phone_2 && <li><a href={`tel:${info.phone_2.replace(/\./g,'')}`} className="hover:text-[color:var(--gold)]">{info.phone_2}</a></li>}
            </ul>
          </div>
          <div>
            <div className="label-tag mb-4">Follow</div>
            <ul className="space-y-2 text-white/70 text-sm">
              {socials.map((s, i) => (
                <li key={i}><a href={s.url} target="_blank" rel="noreferrer" data-testid={`footer-social-${i}`} className="hover:text-[color:var(--gold)] flex items-center gap-2"><s.Icon size={14}/> {s.label}</a></li>
              ))}
              {socials.length === 0 && <li className="text-white/40 italic text-xs">No social links yet.</li>}
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="container-app py-6 flex flex-col md:flex-row items-center justify-between text-xs text-white/40 font-mono">
            <div>© {new Date().getFullYear()} {brand.site_name} & {brand.site_subname}. All rights reserved.</div>
            <div>{brand.tagline}</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
